import { motion } from "framer-motion";
import { Target, Globe, Handshake, Zap, Sprout, Lock, Smartphone, Award } from "lucide-react";
import { useInView } from "@/hooks";
import { missionVisionService, valueService } from "@/services/firebase";
import { useState, useEffect } from "react";
import type { MissionVision, Value } from "@/types";

const iconMap: Record<string, React.ElementType> = {
  target: Target,
  globe: Globe,
  handshake: Handshake,
  zap: Zap,
  sprout: Sprout,
  lock: Lock,
  smartphone: Smartphone,
  award: Award,
};

export function MissionVisionSection() {
  const { ref, isInView } = useInView();
  const [mv, setMv] = useState<MissionVision | null>(null);
  const [values, setValues] = useState<Value[]>([]);

  useEffect(() => {
    Promise.all([
      missionVisionService.getAll().then((items) => setMv(items[0] || null)),
      valueService.getAll().then(setValues),
    ]);
  }, []);

  return (
    <section ref={ref} className="py-20 bg-dark relative overflow-hidden">
      {/* Pattern overlay */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
          >
            <div className="w-14 h-14 bg-primary/20 rounded-xl flex items-center justify-center mb-5">
              <Target className="w-7 h-7 text-primary" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">{mv?.missionTitle || "Notre Mission"}</h3>
            <p className="text-white/70 leading-relaxed">{mv?.missionText || ""}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
          >
            <div className="w-14 h-14 bg-secondary/20 rounded-xl flex items-center justify-center mb-5">
              <Globe className="w-7 h-7 text-secondary" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">{mv?.visionTitle || "Notre Vision"}</h3>
            <p className="text-white/70 leading-relaxed">{mv?.visionText || ""}</p>
          </motion.div>
        </div>

        {/* Values */}
        <motion.h3
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          className="text-center text-2xl font-bold text-white mb-10"
        >
          Nos Valeurs Fondamentales
        </motion.h3>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {values.map((value, index) => {
            const Icon = iconMap[value.icon] || Handshake;
            return (
              <motion.div
                key={value.id}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3 + index * 0.08, duration: 0.5 }}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-colors"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${value.color}20` }}
                >
                  <Icon className="w-5 h-5" style={{ color: value.color }} />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">{value.title}</h4>
                <p className="text-white/60 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
